import type { QualityFormulaType, SpecType } from '@local/lib';

import { AdjustmentMode } from '@local/lib';
import { Chip, Group, Paper, SimpleGrid, Stack, Text, Tooltip } from '@mantine/core';
import { ArrowDown, ArrowUp } from 'lucide-react';

import type { ThresholdFormulaRow } from '../qualityFormulaTypes';

import {
  ADJUSTMENT_MODE_COLOR,
  ADJUSTMENT_MODES,
  AMOUNT_CONFIG_BY_FORMULA_TYPE,
  getSpecUnit,
  rowUsesLowerBoundAsContractedSpec,
} from '../qualityFormulaMetadata';
import { validateRowAmount } from '../qualityFormulaValidation';
import { FormulaField } from './FormulaField';

interface ThresholdRowFieldsProps {
  contractedSpec: number;
  formulaType: QualityFormulaType.Percentage | QualityFormulaType.PerUnit;
  onRowChange: (
    specType: SpecType,
    rowIndex: number,
    field: keyof ThresholdFormulaRow,
    value: boolean | number | string,
  ) => void;
  rows: ThresholdFormulaRow[];
  /** Lays both selected sides out in a row instead of stacking them — only where there's full card
   * width to spare (Contract Price/Deemed Price), not beside Price Components' narrower column. */
  sideBySide?: boolean;
  specType: SpecType;
}

interface ThresholdSideRowProps {
  contractedValue: number;
  formulaType: QualityFormulaType.Percentage | QualityFormulaType.PerUnit;
  onRowChange: (field: keyof ThresholdFormulaRow, value: number | string) => void;
  row: ThresholdFormulaRow;
  specType: SpecType;
}

// One side (Discount or Premium) of a Threshold spec — its bound is entirely derived from the
// shared contracted value above, so this only ever exposes the rate fields to configure. Which
// side(s) are present is controlled entirely by the chip selector above, not from here.
const ThresholdSideRow: React.FC<ThresholdSideRowProps> = ({
  contractedValue,
  formulaType,
  onRowChange,
  row,
  specType,
}) => {
  const amountConfig = AMOUNT_CONFIG_BY_FORMULA_TYPE[formulaType];
  const errors = validateRowAmount(row, formulaType);
  // Which direction Actual has to be in comes from the spec's own higher-value effect (via
  // rowUsesLowerBoundAsContractedSpec) — never hardcoded per adjustmentMode, since e.g. Discount
  // is "above" for Moisture but "at or below" for a premium-effect spec like BTU.
  const appliesAboveContracted = rowUsesLowerBoundAsContractedSpec(row.adjustmentMode, specType);
  // The icon shows the *price* direction (Premium up, Discount down), not the Actual-value
  // direction above — those two can disagree (e.g. Moisture's Discount side is "Actual above" but
  // still a downward price move), so pairing an up-arrow with a red/negative row would read backwards.
  const DirectionIcon = row.adjustmentMode === AdjustmentMode.Premium ? ArrowUp : ArrowDown;
  const priceEffect = row.adjustmentMode === AdjustmentMode.Premium ? 'increases.' : 'decreases.';

  return (
    <Paper
      bg="var(--mantine-color-default-hover)"
      p="sm"
      radius="md"
      shadow="md"
      style={{ borderLeft: `3px solid var(--mantine-color-${ADJUSTMENT_MODE_COLOR[row.adjustmentMode]}-filled)` }}
      withBorder
    >
      <Stack gap="xs">
        <Group gap={4} wrap="nowrap">
          <DirectionIcon
            className={`animate-price-direction animate-price-${row.adjustmentMode === AdjustmentMode.Premium ? 'up' : 'down'}`}
            color={`var(--mantine-color-${ADJUSTMENT_MODE_COLOR[row.adjustmentMode]}-filled)`}
            size={14}
            strokeWidth={3.0}
          />
          <Text c="var(--mantine-color-text)" fz="xs">
            If Actual is {appliesAboveContracted ? 'above' : 'at or below'} {contractedValue}, price {priceEffect}
          </Text>
        </Group>
        <Group align="flex-start" grow>
          <FormulaField
            error={errors.amount}
            fallback={0}
            label={amountConfig.label}
            onChange={value => {
              onRowChange(amountConfig.field, value);
            }}
            tooltip={amountConfig.hint}
            value={row[amountConfig.field]}
          />
          <FormulaField
            error={errors.perIncrement}
            fallback={0}
            label={`Per increment (${getSpecUnit(specType)})`}
            min={0}
            onChange={value => {
              onRowChange('perIncrement', value);
            }}
            tooltip={`Price applies per this many ${getSpecUnit(specType)} of difference (usually 1)`}
            value={row.perIncrement}
          />
        </Group>
      </Stack>
    </Paper>
  );
};

interface ThresholdAppliesToControlProps {
  onSideToggle: (specType: SpecType, adjustmentMode: AdjustmentMode, isVisible: boolean) => void;
  rows: ThresholdFormulaRow[];
  specType: SpecType;
}

// The Discount/Premium chip selector (either side, or both) — split out from ThresholdRowFields
// so QualitySpecCard can place "Applies to" and "Adjustment Behavior" in separate panels (see
// SpecSectionPanel) instead of always stacking them in one block.
export const ThresholdAppliesToControl: React.FC<ThresholdAppliesToControlProps> = ({
  onSideToggle,
  rows,
  specType,
}) => {
  const selectedSides = rows.map(row => row.adjustmentMode);

  const handleSidesChange = (nextSides: string[]) => {
    // At least one side must remain — ignore an attempted change that would leave neither
    // selected (the last remaining chip is also visually disabled, see below).
    if (nextSides.length === 0) return;
    ADJUSTMENT_MODES.forEach(mode => {
      const wasSelected = selectedSides.includes(mode.value);
      const isSelected = nextSides.includes(mode.value);
      if (wasSelected !== isSelected) {
        onSideToggle(specType, mode.value, isSelected);
      }
    });
  };

  return (
    <Chip.Group multiple onChange={handleSidesChange} value={selectedSides}>
      <Group gap="xs">
        {ADJUSTMENT_MODES.map(mode => {
          const isOnlySide = selectedSides.length === 1 && selectedSides[0] === mode.value;
          // The other, unselected chip when only one side is active — a neutral (uncolored, so it
          // doesn't read as the mode's own red/green) shadow hints it's still clickable, since
          // its sibling just went cursor-locked above. Its background comes from the
          // `.mantine-Chip-label:not([data-checked])` rule in theme.css, not inline here, so
          // Mantine's own hover darkening still applies on top of it.
          const isReselectableHint = selectedSides.length === 1 && selectedSides[0] !== mode.value;
          let chipStyles;
          if (isOnlySide) {
            chipStyles = { label: { cursor: 'not-allowed' } };
          } else if (isReselectableHint) {
            chipStyles = { label: { boxShadow: 'var(--mantine-shadow-sm)' } };
          }

          return (
            <Tooltip
              disabled={!isOnlySide}
              key={mode.value}
              label="At least one side is required"
              position="top"
              withArrow
            >
              {/* Locked (cursor only, still fully colored) once it's the sole remaining side —
                  onClickCapture blocks the toggle itself, since the state guard alone still let
                  the browser's native checkbox-click cursor/affordance suggest it was clickable.
                  The cursor override targets the `label` part specifically — Chip's own CSS sets
                  `cursor: pointer` directly on that inner label, so a `style` on the outer chip
                  wrapper never actually overrides the hover cursor the user sees.
                  The other, unselected chip stays normally clickable to add it back. */}
              <Chip
                color={ADJUSTMENT_MODE_COLOR[mode.value]}
                onClickCapture={
                  isOnlySide
                    ? event => {
                        event.preventDefault();
                      }
                    : undefined
                }
                styles={chipStyles}
                value={mode.value}
                variant="filled"
              >
                {mode.label}
              </Chip>
            </Tooltip>
          );
        })}
      </Group>
    </Chip.Group>
  );
};

// Renders THRESHOLD tier mode's "Adjustment Behavior": one compact side card per selected side.
// The shared contracted value lives in the parent card's top row (see QualitySpecCard) since it
// applies to Range mode too. Bounds are never entered directly here — they're derived from that
// shared value. Which side(s) are present is controlled by ThresholdAppliesToControl, not here.
export const ThresholdRowFields: React.FC<ThresholdRowFieldsProps> = ({
  contractedSpec,
  formulaType,
  onRowChange,
  rows,
  sideBySide,
  specType,
}) => {
  // Discount always renders before Premium regardless of insertion order, matching how the two
  // sides read left-to-right in the legacy system this playground mirrors.
  const orderedRows = [...rows].sort(
    (a, b) =>
      (a.adjustmentMode === AdjustmentMode.Discount ? -1 : 1) - (b.adjustmentMode === AdjustmentMode.Discount ? -1 : 1),
  );
  const sideRows = orderedRows.map(row => {
    const rowIndex = rows.indexOf(row);
    return (
      <ThresholdSideRow
        contractedValue={contractedSpec}
        formulaType={formulaType}
        key={row.id}
        onRowChange={(field, value) => {
          onRowChange(specType, rowIndex, field, value);
        }}
        row={row}
        specType={specType}
      />
    );
  });

  if (sideBySide) {
    return (
      <SimpleGrid cols={{ base: 1, sm: orderedRows.length }} spacing="sm">
        {sideRows}
      </SimpleGrid>
    );
  }

  return <Stack gap="sm">{sideRows}</Stack>;
};
